import { useCreateSocialOffer, useUpdateSocialOffer } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuthorizer } from "@navigations/Authorizer";
import type { ISocialOfferWithFavoriteChallenges } from "@root/api/services/offers/socialOffer/types";
import { useGlobalContext } from "@root/context";
import { useLayoutEffect, useRef } from "react";
import { type DefaultValues, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useModalSocialOfferDocuments } from "./documents";
import { formatFormToSocialOffer, formatSocialOfferToDefaultValues } from "./format";
import { type TModalSocialOfferFormResolver, formSchema } from "./resolver";

const defaultValues: DefaultValues<TModalSocialOfferFormResolver> = {
  name: "",
  permanent: "",
  serviceDates: null,
  challenges: [],
  favoriteChallenges: [],
  financingModes: [],
  description: "",
  beneficiaries: "",
  registrationModalities: "",
  url: "",
  address: null,
  provider: {
    name: "",
    providerId: "",
    website: "",
  },
  contact: null,
  documents: [],
  perimeter: [],
  isRemote: false,
};

const feedbackMessages = {
  successCreate: "L'offre de service a été créée avec succès",
  successCreateWithStructurePartner:
    "L'offre de service a été créée avec succès. Un nouveau partenaire a été créé et ajouté au tableau de bord des partenaires.",
  successDuplicateOnBlank: "L’offre a été créée avec succès, vous pouvez la retrouver dans la page du partenaire.",
  successDuplicateOnBlankWithStructurePartner:
    "L’offre a été créée avec succès, vous pouvez la retrouver dans la page du partenaire. Un nouveau partenaire a été créé et ajouté au tableau de bord des partenaires.",
  successUpdate: "L'offre de service a été mise à jour avec succès",
  errorDocuments: "Une erreur est survenue lors de la mise à jour des documents",
  errorCreate: "Une erreur est survenue lors de la création de l'offre de service",
  errorUpdate: "Une erreur est survenue lors de la mise à jour de l'offre de service",
};

const publishFeedback = (severity: "success" | "error", message: string) => {
  window.publish("feedback", { severity, message });
};

const openInBackground = (url: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";

  const event = new MouseEvent("click", {
    ctrlKey: true,
    metaKey: true,
  });

  link.dispatchEvent(event);
};

export const useModalSocialOffer = (
  open: boolean,
  socialOffer: ISocialOfferWithFavoriteChallenges | null,
  onClose: () => void,
  onUpdate?: (socialOffer: ISocialOfferWithFavoriteChallenges) => void,
  isDuplication?: boolean,
  defaultProvider?: {
    name?: string;
    providerId?: ObjectId;
    website?: string;
  }
) => {
  const { challenges, settingsV2, user } = useGlobalContext();
  const location = useLocation();
  const isPartnerAuthorized = useAuthorizer(["partner.dashboard.visualize"]);
  const isAuthorizedTags = useAuthorizer(["offers.social.besoins.v2"]);
  const [{ loading: loadingCreate }, createSocialOffer] = useCreateSocialOffer();
  const [{ loading: loadingUpdate }, updateSocialOffer] = useUpdateSocialOffer();
  const formRef = useRef<HTMLFormElement | null>(null);
  const hookForm = useForm<TModalSocialOfferFormResolver>({
    defaultValues,
    resolver: yupResolver(formSchema),
    context: { isPartnerAuthorized, isAuthorizedTags },
    shouldFocusError: true,
  });
  const { documentsProgress, loadingFindDocuments, handleSocialOfferDocuments } = useModalSocialOfferDocuments(
    hookForm,
    open,
    socialOffer
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useLayoutEffect(() => {
    if (open) {
      if (socialOffer) {
        hookForm.reset(formatSocialOfferToDefaultValues(socialOffer, challenges, settingsV2.financingModes));
      } else {
        hookForm.reset({ ...defaultValues, provider: defaultProvider });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, socialOffer, defaultProvider]);

  const createOffer = async (data: TModalSocialOfferFormResolver, documents: string[]) => {
    const response = await createSocialOffer({
      ...formatFormToSocialOffer(data, isAuthorizedTags),
      documents,
      createStructurePartner: data.createStructurePartner,
    });
    const structurePartnerCreated = data.createStructurePartner && !data.provider.providerId;
    publishFeedback(
      "success",
      location.pathname.includes("/offer/social/") && isDuplication
        ? structurePartnerCreated
          ? feedbackMessages.successDuplicateOnBlankWithStructurePartner
          : feedbackMessages.successDuplicateOnBlank
        : structurePartnerCreated
          ? feedbackMessages.successCreateWithStructurePartner
          : feedbackMessages.successCreate
    );
    onUpdate?.({
      ...response.data,
      favoriteChallenges: data.favoriteChallenges ?? [],
    });

    if (location.pathname.includes("/offer/social/") && isDuplication) {
      openInBackground(`/offer/social/${response.data.offerId}`);
    }
  };

  const updateOffer = async (
    data: TModalSocialOfferFormResolver,
    documents: string[],
    socialOffer: ISocialOfferWithFavoriteChallenges
  ) => {
    const updatedSocialOffer = {
      offerId: socialOffer.offerId,
      source: socialOffer.source,
      ...formatFormToSocialOffer(data, isAuthorizedTags),
      documents,
    };
    await updateSocialOffer(updatedSocialOffer);
    publishFeedback("success", feedbackMessages.successUpdate);
    if (onUpdate) {
      onUpdate({
        ...updatedSocialOffer,
        spotlightId: socialOffer.spotlightId,
        spotlightDates: socialOffer.spotlightDates,
        perimeter: data.perimeter,
        editor: {
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        publicationUpdatedDate: new Date(),
        organisation: user.organisation._id,
        favoriteChallenges: data.favoriteChallenges ?? [],
        externalId: socialOffer.externalId,
      });
    }
  };

  const handleSubmit = hookForm.handleSubmit(async (data) => {
    try {
      data.favoriteChallenges =
        data.favoriteChallenges?.filter((v) => data.challenges?.some((challenge) => challenge._id === v)) ?? [];
      const documents = await handleSocialOfferDocuments(data.documents);
      if (!socialOffer || isDuplication) {
        await createOffer(data, documents);
      } else {
        await updateOffer(data, documents, socialOffer);
      }
      onClose();
    } catch (error) {
      if (error instanceof Error && error.name === "DOCUMENTS_ACTIONS_ERROR") {
        publishFeedback("error", feedbackMessages.errorDocuments);
      } else if (!socialOffer) {
        publishFeedback("error", feedbackMessages.errorCreate);
      } else {
        publishFeedback("error", feedbackMessages.errorUpdate);
      }
    }
  });

  const onClickSubmit = () => {
    formRef.current?.requestSubmit();
  };

  return {
    hookForm,
    formRef,
    loading: loadingCreate || loadingUpdate || Boolean(documentsProgress),
    documentsProgress,
    loadingFindDocuments,
    handleSubmit,
    onClickSubmit,
  };
};
